/**
 * checkin_notes.ts · 09_APP · OS-006 · capability C35
 *
 * The short notes on the Start here card. CLAUDE'S DRAFT WORDING, NOT YET APPROVED BY EMILY.
 * Proposal: 00_BRAIN/PROPOSALS/CHECKIN_NOTES_2026-10-16.md. When she approves or rewrites it,
 * record the decision in 00_BRAIN/DECISION_LEDGER.md and land her words here.
 *
 * Built only from the gift record (hard rule 16: never fill substance from research).
 * No dashes (hard rule 5). American English (hard rule 19).
 */

export interface CheckinPerson {
  id: string;
  first_name: string | null;
  display_name: string;
  email: string | null;
  last_gift_on: string | null;
  first_gift_on: string | null;
  gift_records: number;
  has_recurring: boolean;
  campaign: string | null;
  processor: string | null;
  last_contact_at: string | null;
  suppressed: boolean;
}

export const CHECKIN_SUBJECT = "A quick thank you...";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July",
  "August", "September", "October", "November", "December"];

function parts(iso: string | null) {
  if (!iso) return null;
  const [y = 0, m = 1] = iso.split("-").map(Number);
  return { year: y, month: MONTHS[m - 1] ?? "" };
}

/** Plain words for how they gave, from the gift record only. */
function how(p: CheckinPerson): { forWhat: string; gaveHow: string } {
  const d = parts(p.last_gift_on);
  const year = d?.year ?? "";
  const c = (p.campaign ?? "").toLowerCase();
  if (p.processor === "big_day_of_giving" || c.includes("big day of giving")) {
    return { forWhat: `for giving during Big Day of Giving ${year}`, gaveHow: `gave during Big Day of Giving ${year}` };
  }
  if (p.processor === "svp_fast_pitch" || c.includes("fast pitch")) {
    return { forWhat: `for supporting us at SVP Fast Pitch ${year}`, gaveHow: `supported us at SVP Fast Pitch ${year}` };
  }
  if (c.includes("giving tuesday")) {
    return { forWhat: "for your Giving Tuesday gift", gaveHow: "gave on Giving Tuesday" };
  }
  if (c.includes("holiday gathering")) {
    return { forWhat: "for your gift at our holiday gathering", gaveHow: "gave at our holiday gathering" };
  }
  const thisYear = new Date().getFullYear();
  const when = d ? (d.year === thisYear ? d.month : `${d.month} ${d.year}`) : "this year";
  return { forWhat: `for your gift in ${when}`, gaveHow: d ? `gave to us in ${d.month} ${d.year}` : "gave to us" };
}

function monthsSince(iso: string | null) {
  if (!iso) return 99;
  return (Date.now() - new Date(iso + "T12:00:00").getTime()) / (30.4 * 86400000);
}

export function draftNote(p: CheckinPerson): string {
  const first = (p.first_name || p.display_name.split(" ")[0] || "").trim();
  const { forWhat, gaveHow } = how(p);
  if (monthsSince(p.last_gift_on) <= 6) {
    return [
      `Hi ${first},`,
      "",
      `I wanted to thank you again ${forWhat}.`,
      "",
      "How have you been?",
      "",
      "With gratitude,",
      "Emily",
    ].join("\n");
  }
  return [
    `Hi ${first},`,
    "",
    `I was looking back at the people who ${gaveHow}, and you were one of them.`,
    "",
    "Thank you again.",
    "",
    "I am curious: how did you first learn about us?",
    "",
    "With gratitude,",
    "Emily",
  ].join("\n");
}

/** Short line under the name: when and how they last gave. Never an amount (QuickBooks owns money). */
export function gaveLine(p: CheckinPerson): string {
  const d = parts(p.last_gift_on);
  const when = d ? `${d.month} ${d.year}` : "date unknown";
  const src = (p.campaign ?? "").replace(/[^\x20-\x7E]/g, "").trim() || SOURCE[p.processor ?? ""] || "";
  const more = p.gift_records > 1 ? ` · ${p.gift_records} gifts on record` : "";
  return `Last gave ${when}${src ? ` · ${src}` : ""}${more}`;
}

const SOURCE: Record<string, string> = {
  big_day_of_giving: "Big Day of Giving",
  svp_fast_pitch: "SVP Fast Pitch",
  zeffy: "Zeffy",
  check: "Check",
  zelle: "Zelle",
  bank_deposit: "Bank deposit",
  in_kind: "In kind",
};

/** Opens a Gmail draft in Emily's own account. Nothing is sent until she presses Send there. */
export function gmailComposeUrl(to: string, body: string): string {
  const q = new URLSearchParams({
    authuser: "emily@connect2change.org",
    view: "cm",
    fs: "1",
    to,
    su: CHECKIN_SUBJECT,
    body,
  });
  return `https://mail.google.com/mail/?${q.toString()}`;
}
